import React, { useState, useEffect, useRef } from 'react';
import { Settings, Save, Globe, Shield, Bell, Mail, CreditCard, Upload, Image, X } from 'lucide-react';
import { platformAPI, fileUrl } from '../../services/api';
import toast from 'react-hot-toast';

export default function SuperAdminSettings() {
  const [settings, setSettings] = useState({
    platform_name: 'Ledger Pro',
    support_email: 'support@ledgerpro.com',
    company_address: '',
    company_phone: '',
    company_website: '',
    default_trial_days: 14,
    max_free_companies: 1,
    require_email_verification: true,
    enable_social_auth: true,
    enable_google_auth: true,
    enable_microsoft_auth: true,
    auto_suspend_on_expiry: false,
    grace_period_days: 3,
    max_login_attempts: 5,
    session_timeout_hours: 24,
  });
  const [logo, setLogo] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  useEffect(() => {
    platformAPI.getSettings().then(data => {
      if (data.platform_name) setSettings(prev => ({ ...prev, platform_name: data.platform_name }));
      if (data.support_email) setSettings(prev => ({ ...prev, support_email: data.support_email }));
      if (data.company_address) setSettings(prev => ({ ...prev, company_address: data.company_address }));
      if (data.company_phone) setSettings(prev => ({ ...prev, company_phone: data.company_phone }));
      if (data.company_website) setSettings(prev => ({ ...prev, company_website: data.company_website }));
      if (data.logo_path) setLogo(data.logo_path);
      if (data.favicon_path) setFavicon(data.favicon_path);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await platformAPI.updateSettings(settings);
      toast.success('Platform settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploading(type);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const dataUrl = reader.result;
          if (type === 'logo') {
            const res = await platformAPI.uploadLogo(dataUrl);
            setLogo(res.logo_path);
            toast.success('Logo updated');
          } else {
            const res = await platformAPI.uploadFavicon(dataUrl);
            setFavicon(res.favicon_path);
            // Update favicon in the browser tab
            const link = document.querySelector("link[rel*='icon']");
            if (link) link.href = fileUrl(res.favicon_path);
            toast.success('Favicon updated');
          }
        } catch (err) {
          toast.error(err.message);
        } finally {
          setUploading(null);
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Upload failed');
      setUploading(null);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div>
      <div className="sa-toolbar">
        <span className="sa-toolbar-title">Platform configuration, branding and security settings</span>
        <button className="sa-btn sa-btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Branding — Logo & Favicon */}
      <div className="sa-card" style={{ marginBottom: 16 }}>
        <div className="sa-card-header">
          <h3 className="sa-card-title"><Image size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Branding — Logo & Favicon</h3>
        </div>
        <div className="sa-card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Logo upload */}
            <div style={{ textAlign: 'center' }}>
              <label className="sa-form-label" style={{ display: 'block', marginBottom: 8 }}>Platform Logo</label>
              <div
                onClick={() => logoInputRef.current?.click()}
                style={{
                  width: 180, height: 100, margin: '0 auto',
                  border: '2px dashed #e2e8f0', borderRadius: 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', overflow: 'hidden', background: '#f8fafc',
                  transition: 'border-color 0.2s',
                }}
              >
                {logo ? (
                  <img src={fileUrl(logo)} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <div style={{ color: '#94a3b8', fontSize: 12 }}>
                    <Upload size={20} style={{ display: 'block', margin: '0 auto 4px' }} />
                    Click to upload
                  </div>
                )}
              </div>
              <input ref={logoInputRef} type="file" accept="image/*" hidden onChange={e => handleImageUpload(e, 'logo')} />
              {uploading === 'logo' && <p style={{ fontSize: 11, color: '#3b82f6', marginTop: 6 }}>Uploading...</p>}
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Shows on login page & sidebar</p>
            </div>

            {/* Favicon upload */}
            <div style={{ textAlign: 'center' }}>
              <label className="sa-form-label" style={{ display: 'block', marginBottom: 8 }}>Browser Favicon</label>
              <div
                onClick={() => faviconInputRef.current?.click()}
                style={{
                  width: 100, height: 100, margin: '0 auto',
                  border: '2px dashed #e2e8f0', borderRadius: 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', overflow: 'hidden', background: '#f8fafc',
                  transition: 'border-color 0.2s',
                }}
              >
                {favicon ? (
                  <img src={fileUrl(favicon)} alt="Favicon" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                ) : (
                  <div style={{ color: '#94a3b8', fontSize: 12 }}>
                    <Upload size={20} style={{ display: 'block', margin: '0 auto 4px' }} />
                    Click to upload
                  </div>
                )}
              </div>
              <input ref={faviconInputRef} type="file" accept="image/*" hidden onChange={e => handleImageUpload(e, 'favicon')} />
              {uploading === 'favicon' && <p style={{ fontSize: 11, color: '#3b82f6', marginTop: 6 }}>Uploading...</p>}
              <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Shows in browser tab</p>
            </div>
          </div>
        </div>
      </div>

      {/* General */}
      <div className="sa-card" style={{ marginBottom: 16 }}>
        <div className="sa-card-header">
          <h3 className="sa-card-title"><Globe size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />General</h3>
        </div>
        <div className="sa-card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="sa-form-group">
              <label className="sa-form-label">Platform Name</label>
              <input className="sa-input" value={settings.platform_name} onChange={e => handleChange('platform_name', e.target.value)} />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Support Email</label>
              <input className="sa-input" type="email" value={settings.support_email} onChange={e => handleChange('support_email', e.target.value)} />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Company Address</label>
              <input className="sa-input" value={settings.company_address} onChange={e => handleChange('company_address', e.target.value)} placeholder="123 Main St, City, Country" />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Company Phone</label>
              <input className="sa-input" value={settings.company_phone} onChange={e => handleChange('company_phone', e.target.value)} placeholder="+1 (555) 123-4567" />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Company Website</label>
              <input className="sa-input" value={settings.company_website} onChange={e => handleChange('company_website', e.target.value)} placeholder="https://yourdomain.com" />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Default Trial Days</label>
              <input className="sa-input" type="number" min="0" max="90" value={settings.default_trial_days} onChange={e => handleChange('default_trial_days', parseInt(e.target.value) || 0)} />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Max Free Companies per User</label>
              <input className="sa-input" type="number" min="1" value={settings.max_free_companies} onChange={e => handleChange('max_free_companies', parseInt(e.target.value) || 1)} />
            </div>
          </div>
        </div>
      </div>

      {/* Authentication */}
      <div className="sa-card" style={{ marginBottom: 16 }}>
        <div className="sa-card-header">
          <h3 className="sa-card-title"><Shield size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Authentication & Security</h3>
        </div>
        <div className="sa-card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { key: 'require_email_verification', label: 'Require Email Verification', desc: 'New users must verify their email before accessing the platform' },
              { key: 'enable_social_auth', label: 'Enable Social Authentication', desc: 'Allow users to sign in with Google or Microsoft' },
              { key: 'enable_google_auth', label: 'Enable Google Sign-In', desc: 'Allow users to authenticate with their Google account' },
              { key: 'enable_microsoft_auth', label: 'Enable Microsoft Sign-In', desc: 'Allow users to authenticate with their Microsoft account' },
            ].map(item => (
              <label key={item.key} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 8,
                border: '1px solid #e2e8f0', cursor: 'pointer',
                background: settings[item.key] ? '#f0fdf4' : '#fff',
              }}>
                <input type="checkbox" checked={settings[item.key]} onChange={e => handleChange(item.key, e.target.checked)} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{item.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Subscription Enforcement */}
      <div className="sa-card" style={{ marginBottom: 16 }}>
        <div className="sa-card-header">
          <h3 className="sa-card-title"><CreditCard size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Subscription Enforcement</h3>
        </div>
        <div className="sa-card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="sa-form-group">
              <label className="sa-form-label">Grace Period After Expiry (days)</label>
              <input className="sa-input" type="number" min="0" max="30" value={settings.grace_period_days} onChange={e => handleChange('grace_period_days', parseInt(e.target.value) || 0)} />
              <small style={{ fontSize: 11, color: '#64748b' }}>Days after expiry before restricting access</small>
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Max Login Attempts</label>
              <input className="sa-input" type="number" min="1" max="20" value={settings.max_login_attempts} onChange={e => handleChange('max_login_attempts', parseInt(e.target.value) || 5)} />
            </div>
            <div className="sa-form-group">
              <label className="sa-form-label">Session Timeout (hours)</label>
              <input className="sa-input" type="number" min="1" max="72" value={settings.session_timeout_hours} onChange={e => handleChange('session_timeout_hours', parseInt(e.target.value) || 24)} />
            </div>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 14px', borderRadius: 8,
              border: '1px solid #e2e8f0', cursor: 'pointer', alignSelf: 'start', marginTop: 22,
            }}>
              <input type="checkbox" checked={settings.auto_suspend_on_expiry} onChange={e => handleChange('auto_suspend_on_expiry', e.target.checked)} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Auto-Suspend on Expiry</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Automatically suspend company after grace period</div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
